/* Enough Phone landing — interactions for ep-* sections
   - [data-ep-type]   typing effect when the heading scrolls into view
   - [data-ep-reveal] children rise in one by one from the bottom
   - [data-ep-rise]   element slides up as its section scrolls into view
   - [data-ep-notify] "notify me" button turns into an email field
   - [data-ep-until]  content swaps to data-ep-after once a date has passed
   - [data-ep-video]  click-to-play YouTube / Vimeo embed
   - scroll motion: colour-changing page background, parallax, zoom-in, horizontal text slide, smooth scroll
*/
(function () {
  if (window.__epLanding) return;
  window.__epLanding = true;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;

  /* ---------- typing ---------- */
  function splitChars(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    var chars = [];
    nodes.forEach(function (node) {
      if (!node.textContent.trim()) return;
      var frag = document.createDocumentFragment();
      Array.from(node.textContent).forEach(function (c) {
        if (/\s/.test(c)) {
          frag.appendChild(document.createTextNode(c));
        } else {
          var s = document.createElement('span');
          s.className = 'ep-ch';
          s.setAttribute('aria-hidden', 'true');
          s.textContent = c;
          frag.appendChild(s);
          chars.push(s);
        }
      });
      node.parentNode.replaceChild(frag, node);
    });
    return chars;
  }

  function typeOut(el) {
    var chars = el.__epChars || [];
    var step = Math.max(28, Math.min(70, 1100 / Math.max(chars.length, 1)));
    var i = 0;
    var prev = null;
    function tick() {
      if (prev) prev.classList.remove('is-cur');
      if (i >= chars.length) {
        el.classList.add('is-typed');
        if (prev) {
          prev.classList.add('is-cur');
          setTimeout(function () { prev.classList.remove('is-cur'); }, 1800);
        }
        return;
      }
      var c = chars[i++];
      c.classList.add('is-on', 'is-cur');
      prev = c;
      setTimeout(tick, step);
    }
    tick();
  }

  function initType(root) {
    root.querySelectorAll('[data-ep-type]').forEach(function (el) {
      if (el.__epTyped) return;
      el.__epTyped = true;
      if (reduced || !hasIO) { el.classList.add('is-typed'); return; }
      el.setAttribute('aria-label', (el.innerText || el.textContent).replace(/\s+/g, ' ').trim());
      el.__epChars = splitChars(el);
      el.classList.add('is-armed');
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { io.disconnect(); typeOut(el); }
        });
      }, { threshold: 0.5 });
      io.observe(el);
    });
  }

  /* ---------- reveal ---------- */
  function initReveal(root) {
    root.querySelectorAll('[data-ep-reveal]').forEach(function (el) {
      if (el.__epReveal) return;
      el.__epReveal = true;
      Array.from(el.children).forEach(function (child, i) { child.style.setProperty('--i', i); });
      if (reduced || !hasIO) return;
      el.classList.add('is-armed');
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { io.disconnect(); el.classList.add('is-in'); }
        });
      }, { threshold: 0.25 });
      io.observe(el);
    });
  }

  /* ---------- rise on scroll ---------- */
  var risers = [];
  var ticking = false;
  function updateRisers() {
    ticking = false;
    var vh = window.innerHeight;
    risers.forEach(function (el) {
      if (!el.isConnected) return;
      var host = el.closest('.ep') || el;
      var r = host.getBoundingClientRect();
      var p = (vh - r.top) / (vh * 0.85);
      p = Math.max(0, Math.min(1, p));
      var ease = 1 - Math.pow(1 - p, 3);
      var dist = parseFloat(el.getAttribute('data-ep-rise')) || 45;
      el.style.transform = 'translate3d(0,' + ((1 - ease) * dist).toFixed(2) + '%,0)';
    });
  }
  function onScroll() {
    if (!ticking) { ticking = true; requestAnimationFrame(updateRisers); }
  }
  function initRise(root) {
    if (reduced) return;
    root.querySelectorAll('[data-ep-rise]').forEach(function (el) {
      if (risers.indexOf(el) === -1) risers.push(el);
    });
    if (risers.length && !window.__epRiseBound) {
      window.__epRiseBound = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
    }
    updateRisers();
  }

  /* ---------- notify me ----------
     The opener is [data-ep-notify-open] when the snippet provides one, and only
     otherwise the first .ep-btn. In Kickstarter mode the first .ep-btn is the
     campaign link, so binding to it would flash the email field open on every
     click through to Kickstarter. */
  function initNotify(root) {
    root.querySelectorAll('[data-ep-notify]').forEach(function (wrap) {
      if (wrap.__epNotify) return;
      wrap.__epNotify = true;
      var btn = wrap.querySelector('[data-ep-notify-open]') || wrap.querySelector('.ep-btn');
      if (!btn) return;
      btn.addEventListener('click', function () {
        wrap.classList.add('is-open');
        var input = wrap.querySelector('input[type="email"]');
        if (input) setTimeout(function () { input.focus(); }, 30);
      });
    });
  }

  /* ---------- time-limited copy ----------
     [data-ep-until="2026-12-04T10:00:00-08:00"] replaces its content with the
     HTML in data-ep-after once that moment has passed, so campaign copy can't
     outlive the campaign if nobody edits the page. Checked on load only: a
     visitor who keeps the tab open across the deadline is not worth a timer.
     An empty or unparseable date never swaps. */
  function initUntil(root) {
    var now = Date.now();
    root.querySelectorAll('[data-ep-until]').forEach(function (el) {
      var until = Date.parse(el.getAttribute('data-ep-until') || '');
      if (isNaN(until) || now < until) return;
      var after = el.getAttribute('data-ep-after');
      if (after === null) return;
      el.innerHTML = after;
      el.removeAttribute('data-ep-until');
    });
  }

  /* ---------- video ---------- */
  function initVideo(root) {
    root.querySelectorAll('[data-ep-video]').forEach(function (btn) {
      if (btn.__epVideo) return;
      btn.__epVideo = true;
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-ep-video');
        var type = btn.getAttribute('data-ep-video-type') || 'youtube';
        var frame = btn.parentNode;
        var iframe = document.createElement('iframe');
        iframe.src = type === 'vimeo'
          ? 'https://player.vimeo.com/video/' + encodeURIComponent(id) + '?autoplay=1'
          : 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0&playsinline=1&origin=' + encodeURIComponent(location.origin);
        iframe.title = btn.getAttribute('aria-label') || 'Video';
        iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        // YouTube refuses to play (error 153 "player configuration") when no referrer is sent
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        frame.appendChild(iframe);
        btn.remove();
      });
    });
  }


  /* ---------- scroll motion (sofi-style) ----------
     [data-ep-morph]   section shares one page colour that changes as you scroll
                       (data-ep-bg = colour, data-ep-tone = light|dark)
     [data-ep-parallax="0.1"]  element drifts slower than the page
     [data-ep-zoom="1.15"]     element scales down to 1 as it enters
     [data-ep-slide="18"]      element slides horizontally (percent) while passing
  */
  var morphs = [], parallaxes = [], zooms = [], slides = [];
  var motionTicking = false;
  var rootEl = document.documentElement;
  var activeMorph = null;

  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

  function updateMotion() {
    motionTicking = false;
    var vh = window.innerHeight;

    if (morphs.length) {
      var probe = vh * 0.55, hit = null;
      for (var i = 0; i < morphs.length; i++) {
        var r = morphs[i].getBoundingClientRect();
        if (r.top <= probe && r.bottom > probe) { hit = morphs[i]; break; }
      }
      if (hit && hit !== activeMorph) {
        activeMorph = hit;
        rootEl.style.setProperty('--ep-page-bg', hit.getAttribute('data-ep-bg'));
        rootEl.setAttribute('data-ep-tone', hit.getAttribute('data-ep-tone') || 'light');
      }
    }

    if (reduced) return;

    parallaxes.forEach(function (el) {
      var host = el.parentElement || el;
      var r = host.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) return;
      var f = parseFloat(el.getAttribute('data-ep-parallax')) || 0.1;
      var d = (r.top + r.height / 2) - vh / 2;
      el.style.transform = 'translate3d(0,' + (d * f).toFixed(1) + 'px,0)';
    });

    zooms.forEach(function (el) {
      var r = el.parentElement.getBoundingClientRect();
      var p = clamp((vh - r.top) / (vh + r.height * 0.5), 0, 1);
      var from = parseFloat(el.getAttribute('data-ep-zoom')) || 1.15;
      el.style.transform = 'scale(' + (from - (from - 1) * p).toFixed(4) + ')';
    });

    slides.forEach(function (el) {
      var r = el.getBoundingClientRect();
      var p = clamp((vh - r.top) / (vh + r.height), 0, 1);
      var amt = parseFloat(el.getAttribute('data-ep-slide')) || 15;
      el.style.transform = 'translate3d(' + (amt - 2 * amt * p).toFixed(2) + '%,0,0)';
    });
  }
  function onMotionScroll() {
    if (!motionTicking) { motionTicking = true; requestAnimationFrame(updateMotion); }
  }
  function collect(root, sel, list) {
    root.querySelectorAll(sel).forEach(function (el) { if (list.indexOf(el) === -1) list.push(el); });
  }
  function initMotion(root) {
    morphs = morphs.filter(function (el) { return el.isConnected; });
    collect(root, '[data-ep-morph]', morphs);
    morphs.sort(function (a, b) { return a.compareDocumentPosition(b) & 2 ? 1 : -1; });
    collect(root, '[data-ep-parallax]', parallaxes);
    collect(root, '[data-ep-zoom]', zooms);
    collect(root, '[data-ep-slide]', slides);
    if (morphs.length) rootEl.classList.add('ep-has-morph');
    if (!window.__epMotionBound) {
      window.__epMotionBound = true;
      window.addEventListener('scroll', onMotionScroll, { passive: true });
      window.addEventListener('resize', onMotionScroll);
    }
    activeMorph = null;
    updateMotion();
    // first paint without colour transition, then enable it
    requestAnimationFrame(function () { rootEl.classList.add('ep-morph-ready'); });
  }

  /* ---------- smooth (inertia) scrolling ---------- */
  function initSmooth() {
    if (window.__epSmooth) return;
    var flag = document.querySelector('[data-ep-smooth]');
    if (!flag || reduced) return;
    if (window.Shopify && window.Shopify.designMode) return;           // never inside the theme editor
    if (!window.matchMedia('(pointer: fine)').matches) return;          // desktop only; phones keep native scroll
    window.__epSmooth = true;
    var s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/lenis@1.1.13/dist/lenis.min.js';
    s.async = true;
    s.onload = function () {
      if (!window.Lenis) return;
      var lenis = new window.Lenis({ duration: 1.15, smoothWheel: true });
      window.__epLenis = lenis;
      function raf(t) { lenis.raf(t); requestAnimationFrame(raf); }
      requestAnimationFrame(raf);
    };
    document.head.appendChild(s);
  }

  function init(root) {
    root = root || document;
    initUntil(root);
    initType(root);
    initReveal(root);
    initRise(root);
    initNotify(root);
    initVideo(root);
    initMotion(root);
    initSmooth();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  // Theme editor: re-run when a section is re-rendered
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
